import { useMemo, useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import {
  Activity,
  Bot,
  ChevronDown,
  ChevronRight,
  Cpu,
  FileText,
  Gauge,
  HeartPulse,
  LayoutDashboard,
  ListChecks,
  Move3d,
  Satellite,
  SlidersHorizontal,
  Wrench,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/Badge';
import { FleetSidebar } from '@/components/layout/FleetSidebar';
import { ActionCards } from '@/components/assets/ActionCards';
import { CalibrationCenter } from '@/components/assets/CalibrationCenter';
import { DroneViewer3D } from '@/components/assets/DroneViewer3D';
import { ParamBrowser } from '@/components/assets/ParamBrowser';
import { PreflightBoard } from '@/components/assets/PreflightBoard';
import { SensorPanels } from '@/components/assets/SensorPanels';
import { SetupChecklist, type AssetTab } from '@/components/assets/SetupChecklist';
import { VehicleLog } from '@/components/assets/VehicleLog';
import { lintParams, type LintFinding } from '@/components/assets/paramLint';
import { paramMeta } from '@/components/assets/paramMeta';
import { useFleet } from '@/store/fleet';
import { useParams } from '@/store/params';
import { useSelection } from '@/store/selection';
import { formatFlightMode } from '@/lib/flightMode';
import { cn } from '@/lib/utils';
import { rootRoute } from './__root';

const NAV: { id: AssetTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'summary', label: 'Summary', icon: LayoutDashboard },
  { id: 'setup', label: 'Setup', icon: ListChecks },
  { id: 'calibrate', label: 'Calibrate', icon: Move3d },
  { id: 'params', label: 'Params', icon: SlidersHorizontal },
  { id: 'health', label: 'Health', icon: HeartPulse },
  { id: 'logs', label: 'Logs', icon: FileText },
];

function AssetsWorkspace() {
  const robots = useFleet((s) => s.robots);
  const states = useFleet((s) => s.states);
  const selected = useSelection((s) => s.selected);
  const focusedId =
    selected.size > 0 ? (selected.values().next().value as string) : Object.keys(robots)[0] ?? null;
  const robot = focusedId ? robots[focusedId] : undefined;
  const state = focusedId ? states[focusedId] : undefined;
  const [tab, setTab] = useState<AssetTab>('summary');

  return (
    <div className="flex h-full w-full">
      <FleetSidebar commandDock={false} />
      <main className="flex flex-1 overflow-hidden bg-base">
        {!robot ? (
          <EmptySelection />
        ) : (
          <>
            {/* Sub-nav — QGC-style vehicle-setup rail */}
            <nav className="flex w-40 flex-none flex-col border-r border-border-default bg-surface-1 py-2">
              <div className="mb-2 px-3">
                <div className="truncate font-mono text-[13px] text-fg-primary">{robot.name}</div>
                <div className="truncate font-mono text-[9.5px] uppercase tracking-[0.08em] text-fg-muted">
                  {robot.id} · {formatFlightMode(state?.mode)}
                </div>
              </div>
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = tab === n.id;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => setTab(n.id)}
                    className={cn(
                      'relative flex items-center gap-2.5 px-3 py-2 text-left text-[12.5px] transition-colors',
                      active
                        ? 'bg-surface-2 font-medium text-fg-primary'
                        : 'text-fg-secondary hover:bg-surface-2/60 hover:text-fg-primary',
                    )}
                  >
                    {active && (
                      <span
                        className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full"
                        style={{ background: 'var(--color-accent)' }}
                      />
                    )}
                    <Icon className={cn('h-4 w-4', active ? 'text-accent' : 'text-fg-muted')} />
                    {n.label}
                  </button>
                );
              })}
              <div className="mt-auto px-3 pt-3">
                <Link
                  to="/assistant"
                  className="flex items-center gap-2 rounded border border-border-default bg-surface-2/40 px-2 py-1.5 text-[11px] text-fg-secondary hover:border-accent/40 hover:text-fg-primary"
                  title='Ask Diva — "check my params", "why does it circle in loiter?"'
                >
                  <Bot className="h-3.5 w-3.5 text-accent" />
                  Ask Diva
                </Link>
              </div>
            </nav>

            {/* Tab body */}
            <section className="flex min-w-0 flex-1 flex-col overflow-hidden">
              {tab === 'summary' && <SummaryTab robotId={robot.id} />}
              {tab === 'setup' && (
                <div className="overflow-y-auto overflow-x-hidden">
                  <SetupChecklist robotId={robot.id} onNavigate={setTab} />
                  <div className="mx-auto max-w-[760px] px-4 pb-6">
                    <h3 className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">
                      Bench actions
                    </h3>
                    <ActionCards robotId={robot.id} />
                  </div>
                </div>
              )}
              {tab === 'calibrate' && (
                <div className="overflow-y-auto overflow-x-hidden">
                  <CalibrationCenter robotId={robot.id} />
                </div>
              )}
              {tab === 'params' && (
                <div className="flex min-h-0 flex-1 flex-col">
                  <ConfigCheckBanner robotId={robot.id} />
                  <div className="min-h-0 flex-1">
                    <ParamBrowser robot={robot} />
                  </div>
                </div>
              )}
              {tab === 'health' && (
                <div className="grid flex-1 grid-cols-1 gap-3 overflow-y-auto overflow-x-hidden p-3 lg:grid-cols-2">
                  <div className="space-y-3">
                    <PreflightBoard state={state} />
                  </div>
                  <SensorPanels robot={robot} state={state} />
                </div>
              )}
              {tab === 'logs' && <VehicleLog robotId={robot.id} />}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

/* =================================================================== */
/* Summary — attitude viewer + status tiles                             */
/* =================================================================== */

function SummaryTab({ robotId }: { robotId: string }) {
  const robot = useFleet((s) => s.robots[robotId]);
  const state = useFleet((s) => s.states[robotId]);
  const params = useParams((s) => s.byRobot[robotId] ?? {});
  const loaded = Object.keys(params).length > 0;

  const v = (id: string) => params[id]?.value;
  const zeroed = (ids: string[]) =>
    ids.every((id) => v(id) !== undefined) && ids.every((id) => v(id) === 0);

  const frameLabel = useMemo(() => {
    const fc = v('FRAME_CLASS');
    if (fc === undefined) return null;
    const meta = paramMeta('FRAME_CLASS');
    return meta?.values?.find((x) => x.value === fc)?.label ?? `class ${fc}`;
  }, [params]);

  const accelCal = loaded ? !zeroed(['INS_ACCOFFS_X', 'INS_ACCOFFS_Y', 'INS_ACCOFFS_Z']) : null;
  const compassCal = loaded ? !zeroed(['COMPASS_OFS_X', 'COMPASS_OFS_Y', 'COMPASS_OFS_Z']) : null;
  const findings = useMemo(() => lintParams(params), [params]);
  const criticals = findings.filter((f) => f.severity === 'critical').length;

  return (
    <div className="grid flex-1 grid-cols-1 gap-3 overflow-y-auto overflow-x-hidden p-3 lg:grid-cols-2">
      <div className="relative flex min-h-[380px] flex-col rounded border border-border-default bg-surface-1">
        {robot && <DroneViewer3D robot={robot} state={state} />}
      </div>
      <div className="grid content-start gap-2 sm:grid-cols-2">
        <Tile
          icon={Cpu}
          label="Firmware"
          value={state?.firmware?.flight_sw ?? '—'}
          hint={robot ? `${(robot.kind.frame as any)?.platform ?? 'craft'}` : ''}
        />
        <Tile
          icon={Wrench}
          label="Frame"
          value={frameLabel ?? (loaded ? 'unset' : 'load params')}
          hint="FRAME_CLASS"
          tone={frameLabel ? 'default' : 'warn'}
        />
        <Tile
          icon={Move3d}
          label="Accel cal"
          value={accelCal === null ? '—' : accelCal ? 'calibrated' : 'REQUIRED'}
          hint="INS_ACCOFFS"
          tone={accelCal === false ? 'critical' : accelCal ? 'good' : 'default'}
        />
        <Tile
          icon={Activity}
          label="Compass cal"
          value={compassCal === null ? '—' : compassCal ? 'calibrated' : 'REQUIRED'}
          hint="COMPASS_OFS"
          tone={compassCal === false ? 'warn' : compassCal ? 'good' : 'default'}
        />
        <Tile
          icon={Satellite}
          label="GPS"
          value={state?.gps ? `${state.gps.fix} · ${state.gps.satellites} sats` : '—'}
          hint={state?.gps?.hdop != null ? `hdop ${state.gps.hdop.toFixed(1)}` : ''}
          tone={
            state?.gps && state.gps.fix !== 'none' && state.gps.fix !== 'fix2d' ? 'good' : 'warn'
          }
        />
        <Tile
          icon={Gauge}
          label="Config check"
          value={
            !loaded ? 'load params' : findings.length === 0 ? 'clean' : `${findings.length} findings`
          }
          hint={criticals > 0 ? `${criticals} critical` : ''}
          tone={criticals > 0 ? 'critical' : findings.length > 0 ? 'warn' : loaded ? 'good' : 'default'}
        />
      </div>
    </div>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  hint,
  tone = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'good' | 'warn' | 'critical';
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-1 p-3">
      <div className="flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-fg-muted">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div
        className={cn(
          'mt-1 truncate font-mono text-[15px]',
          tone === 'default' && 'text-fg-primary',
          tone === 'good' && 'text-status-good',
          tone === 'warn' && 'text-status-warn',
          tone === 'critical' && 'text-status-critical',
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 truncate font-mono text-[9.5px] text-fg-muted">{hint}</div>}
    </div>
  );
}

/* =================================================================== */
/* Config-check banner above the param table                            */
/* =================================================================== */

function ConfigCheckBanner({ robotId }: { robotId: string }) {
  const params = useParams((s) => s.byRobot[robotId] ?? {});
  const [open, setOpen] = useState(false);
  const findings = useMemo(() => lintParams(params), [params]);
  if (Object.keys(params).length === 0 || findings.length === 0) return null;

  const counts = {
    critical: findings.filter((f) => f.severity === 'critical').length,
    warning: findings.filter((f) => f.severity === 'warning').length,
    info: findings.filter((f) => f.severity === 'info').length,
  };

  return (
    <div className="flex-none border-b border-border-default bg-surface-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-fg-muted" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-fg-muted" />
        )}
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-fg-primary">
          Config check
        </span>
        {counts.critical > 0 && <Badge tone="critical">{counts.critical} critical</Badge>}
        {counts.warning > 0 && <Badge tone="warn">{counts.warning} warning</Badge>}
        {counts.info > 0 && <Badge tone="default">{counts.info} info</Badge>}
        <span className="ml-auto text-[10px] text-fg-muted">
          rule-based · ask Diva "check my params" for the same report in chat
        </span>
      </button>
      {open && (
        <ul className="max-h-56 space-y-2 overflow-y-auto border-t border-border-subtle px-3 py-2.5">
          {findings.map((f) => (
            <FindingRow key={f.title} finding={f} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FindingRow({ finding }: { finding: LintFinding }) {
  return (
    <li className="flex items-start gap-2">
      <span
        className={cn(
          'mt-1 h-2 w-2 flex-none rounded-full',
          finding.severity === 'critical' && 'bg-status-critical',
          finding.severity === 'warning' && 'bg-status-warn',
          finding.severity === 'info' && 'bg-fg-muted',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-medium text-fg-primary">{finding.title}</div>
        <div className="text-[11px] leading-snug text-fg-secondary">{finding.detail}</div>
        <div className="mt-0.5 text-[11px] text-fg-muted">
          <span className="text-accent">Fix:</span> {finding.fix}
          <span className="ml-2 font-mono text-[9.5px]">{finding.params.join(' · ')}</span>
        </div>
      </div>
    </li>
  );
}

function EmptySelection() {
  return (
    <div className="grid h-full w-full place-items-center">
      <div className="text-center">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-fg-muted">
          no asset selected
        </div>
        <p className="mt-2 text-[12px] text-fg-secondary">
          Pick a robot from the fleet sidebar to set it up: params, calibration, health, logs.
        </p>
      </div>
    </div>
  );
}

export const assetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/assets',
  component: AssetsWorkspace,
});
